import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { BibAssignPreview } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputInt,
    ValidatedInt,
    DataSourceLookup,
    InputText,
    ValidatedText,
    InputNullableDateTime,
    DateComp,
    InputBool,
    DisplayBool,
} from '@/components';
import { useContestantStore, useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { type DateType } from '@/utilities/dateutils';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class BibAssignPreviewGuiDef extends GuiDef<BibAssignPreview> {
    constructor() {
        super('BibAssignPreview');

        const Contestant = storeToRefs(useContestantStore());
        const ContestantFullNameWithEmptySource = computed(() => {
            return Contestant.ContestantFullNameWithEmptyAsSelectLine.value;
        });
        const EventCommon = storeToRefs(useEventCommonStore());
        const GroupsWithAllSource = computed(() => {
            return EventCommon.GroupsWithAllAsSelectLine.value;
        });
        const CategoriesWithAllSource = computed(() => {
            return EventCommon.CategoriesWithAllAsSelectLine.value;
        });

        const Row_num = new FieldDef<BibAssignPreview>();
        Row_num.Name = 'Row_num';
        Row_num.FieldName = 'Row_num';
        Row_num.DefinedFor = 'Both';
        Row_num.TranslationKey = 'general.bib';
        Row_num.Editable = true;
        Row_num.Filterable = false;
        Row_num.Sortable = true;
        Row_num.CssClasses = '';
        Row_num.value = (bibassignpreview) => bibassignpreview.Row_num;
        Row_num.setValue = (bibassignpreview, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            bibassignpreview.Row_num = value;
        };
        Row_num.DetailEditor = markRaw(InputInt);
        Row_num.DetailEditorProps = {};
        Row_num.CellEditor = markRaw(InputInt);
        Row_num.CellEditorProps = {};
        Row_num.CellRenderer = markRaw(ValidatedInt);
        Row_num.CellRendererProps = {};
        Row_num.Validator = null;
        Row_num.SortDirection = undefined;
        Row_num.cellClassRules = undefined;
        Row_num.MinWidth = undefined;
        Row_num.MaxWidth = undefined;
        Row_num.Tooltip = null;
        this.Fields.push(Row_num);

        const ContestantID = new FieldDef<BibAssignPreview>();
        ContestantID.Name = 'ContestantID';
        ContestantID.FieldName = 'ContestantID';
        ContestantID.DefinedFor = 'Both';
        ContestantID.TranslationKey = 'bibassignpreview.contestantid';
        ContestantID.Editable = false;
        ContestantID.Filterable = false;
        ContestantID.Sortable = true;
        ContestantID.CssClasses = '';
        ContestantID.value = (bibassignpreview) => bibassignpreview.ContestantID;
        ContestantID.setValue = () => {};
        // @ts-expect-error @ts-ignore
        ContestantID.CellRenderer = markRaw(DataSourceLookup);
        ContestantID.CellRendererProps = { datasource: { mapsource: ContestantFullNameWithEmptySource }, readonly: true };
        ContestantID.ExportValueFormatter = (obj: BibAssignPreview) => {
            return ContestantFullNameWithEmptySource.value.get(obj.ContestantID)?.text;
        };
        ContestantID.Validator = null;
        ContestantID.SortDirection = undefined;
        ContestantID.cellClassRules = undefined;
        ContestantID.MinWidth = undefined;
        ContestantID.MaxWidth = undefined;
        ContestantID.Tooltip = null;
        this.Fields.push(ContestantID);

        const GroupID = new FieldDef<BibAssignPreview>();
        GroupID.Name = 'GroupID';
        GroupID.FieldName = 'GroupID';
        GroupID.DefinedFor = 'Both';
        GroupID.TranslationKey = 'bibassignpreview.groupid';
        GroupID.Editable = false;
        GroupID.Filterable = false;
        GroupID.Sortable = true;
        GroupID.CssClasses = '';
        GroupID.value = (bibassignpreview) => bibassignpreview.GroupID;
        GroupID.setValue = () => {};
        // @ts-expect-error @ts-ignore
        GroupID.CellRenderer = markRaw(DataSourceLookup);
        GroupID.CellRendererProps = { datasource: { mapsource: GroupsWithAllSource }, readonly: true };
        GroupID.ExportValueFormatter = (obj: BibAssignPreview) => {
            return GroupsWithAllSource.value.get(obj.GroupID)?.text;
        };
        GroupID.Validator = null;
        GroupID.SortDirection = undefined;
        GroupID.cellClassRules = undefined;
        GroupID.MinWidth = undefined;
        GroupID.MaxWidth = undefined;
        GroupID.Tooltip = null;
        this.Fields.push(GroupID);

        const CategoryID = new FieldDef<BibAssignPreview>();
        CategoryID.Name = 'CategoryID';
        CategoryID.FieldName = 'CategoryID';
        CategoryID.DefinedFor = 'Both';
        CategoryID.TranslationKey = 'bibassignpreview.categoryid';
        CategoryID.Editable = false;
        CategoryID.Filterable = false;
        CategoryID.Sortable = true;
        CategoryID.CssClasses = '';
        CategoryID.value = (bibassignpreview) => bibassignpreview.CategoryID;
        CategoryID.setValue = () => {};
        // @ts-expect-error @ts-ignore
        CategoryID.CellRenderer = markRaw(DataSourceLookup);
        CategoryID.CellRendererProps = { datasource: { mapsource: CategoriesWithAllSource }, readonly: true };
        CategoryID.ExportValueFormatter = (obj: BibAssignPreview) => {
            return CategoriesWithAllSource.value.get(obj.CategoryID)?.text;
        };
        CategoryID.Validator = null;
        CategoryID.SortDirection = undefined;
        CategoryID.cellClassRules = undefined;
        CategoryID.MinWidth = undefined;
        CategoryID.MaxWidth = undefined;
        CategoryID.Tooltip = null;
        this.Fields.push(CategoryID);

        const SourceName = new FieldDef<BibAssignPreview>();
        SourceName.Name = 'SourceName';
        SourceName.FieldName = 'SourceName';
        SourceName.DefinedFor = 'Both';
        SourceName.TranslationKey = 'bibassignpreview.sourcename';
        SourceName.Editable = false;
        SourceName.Filterable = true;
        SourceName.Sortable = true;
        SourceName.CssClasses = '';
        SourceName.value = (bibassignpreview) => bibassignpreview.SourceName;
        SourceName.setValue = () => {};
        SourceName.DetailEditor = markRaw(InputText);
        SourceName.DetailEditorProps = {};
        SourceName.CellEditor = markRaw(InputText);
        SourceName.CellEditorProps = {};
        SourceName.CellRenderer = markRaw(ValidatedText);
        SourceName.CellRendererProps = {};
        SourceName.Validator = null;
        SourceName.SortDirection = undefined;
        SourceName.cellClassRules = undefined;
        SourceName.MinWidth = undefined;
        SourceName.MaxWidth = undefined;
        SourceName.Tooltip = null;
        this.Fields.push(SourceName);

        const Sort = new FieldDef<BibAssignPreview>();
        Sort.Name = 'Sort';
        Sort.FieldName = 'Sort';
        Sort.DefinedFor = 'Both';
        Sort.TranslationKey = 'bibassignpreview.sort';
        Sort.Editable = false;
        Sort.Filterable = false;
        Sort.Sortable = true;
        Sort.CssClasses = '';
        Sort.value = (bibassignpreview) => bibassignpreview.Sort;
        Sort.setValue = () => {};
        Sort.DetailEditor = markRaw(InputInt);
        Sort.DetailEditorProps = {};
        Sort.CellEditor = markRaw(InputInt);
        Sort.CellEditorProps = {};
        Sort.CellRenderer = markRaw(ValidatedInt);
        Sort.CellRendererProps = {};
        Sort.Validator = null;
        Sort.SortDirection = undefined;
        Sort.cellClassRules = undefined;
        Sort.MinWidth = undefined;
        Sort.MaxWidth = undefined;
        Sort.Tooltip = null;
        this.Fields.push(Sort);

        const Seeding1 = new FieldDef<BibAssignPreview>();
        Seeding1.Name = 'Seeding1';
        Seeding1.FieldName = 'Seeding1';
        Seeding1.DefinedFor = 'Both';
        Seeding1.TranslationKey = 'bibassignpreview.seeding1';
        Seeding1.Editable = false;
        Seeding1.Filterable = true;
        Seeding1.Sortable = true;
        Seeding1.CssClasses = '';
        Seeding1.value = (bibassignpreview) => bibassignpreview.Seeding1;
        Seeding1.setValue = () => {};
        Seeding1.DetailEditor = markRaw(InputText);
        Seeding1.DetailEditorProps = {};
        Seeding1.CellEditor = markRaw(InputText);
        Seeding1.CellEditorProps = {};
        Seeding1.CellRenderer = markRaw(ValidatedText);
        Seeding1.CellRendererProps = {};
        Seeding1.Validator = null;
        Seeding1.SortDirection = undefined;
        Seeding1.cellClassRules = undefined;
        Seeding1.MinWidth = undefined;
        Seeding1.MaxWidth = undefined;
        Seeding1.Tooltip = null;
        this.Fields.push(Seeding1);

        const Seeding2 = new FieldDef<BibAssignPreview>();
        Seeding2.Name = 'Seeding2';
        Seeding2.FieldName = 'Seeding2';
        Seeding2.DefinedFor = 'Both';
        Seeding2.TranslationKey = 'bibassignpreview.seeding2';
        Seeding2.Editable = false;
        Seeding2.Filterable = true;
        Seeding2.Sortable = true;
        Seeding2.CssClasses = '';
        Seeding2.value = (bibassignpreview) => bibassignpreview.Seeding2;
        Seeding2.setValue = () => {};
        Seeding2.DetailEditor = markRaw(InputText);
        Seeding2.DetailEditorProps = {};
        Seeding2.CellEditor = markRaw(InputText);
        Seeding2.CellEditorProps = {};
        Seeding2.CellRenderer = markRaw(ValidatedText);
        Seeding2.CellRendererProps = {};
        Seeding2.Validator = null;
        Seeding2.SortDirection = undefined;
        Seeding2.cellClassRules = undefined;
        Seeding2.MinWidth = undefined;
        Seeding2.MaxWidth = undefined;
        Seeding2.Tooltip = null;
        this.Fields.push(Seeding2);

        const Seeding3 = new FieldDef<BibAssignPreview>();
        Seeding3.Name = 'Seeding3';
        Seeding3.FieldName = 'Seeding3';
        Seeding3.DefinedFor = 'Both';
        Seeding3.TranslationKey = 'bibassignpreview.seeding3';
        Seeding3.Editable = false;
        Seeding3.Filterable = true;
        Seeding3.Sortable = true;
        Seeding3.CssClasses = '';
        Seeding3.value = (bibassignpreview) => bibassignpreview.Seeding3;
        Seeding3.setValue = () => {};
        Seeding3.DetailEditor = markRaw(InputText);
        Seeding3.DetailEditorProps = {};
        Seeding3.CellEditor = markRaw(InputText);
        Seeding3.CellEditorProps = {};
        Seeding3.CellRenderer = markRaw(ValidatedText);
        Seeding3.CellRendererProps = {};
        Seeding3.Validator = null;
        Seeding3.SortDirection = undefined;
        Seeding3.cellClassRules = undefined;
        Seeding3.MinWidth = undefined;
        Seeding3.MaxWidth = undefined;
        Seeding3.Tooltip = null;
        this.Fields.push(Seeding3);

        const Seeding4 = new FieldDef<BibAssignPreview>();
        Seeding4.Name = 'Seeding4';
        Seeding4.FieldName = 'Seeding4';
        Seeding4.DefinedFor = 'Both';
        Seeding4.TranslationKey = 'bibassignpreview.seeding4';
        Seeding4.Editable = false;
        Seeding4.Filterable = true;
        Seeding4.Sortable = true;
        Seeding4.CssClasses = '';
        Seeding4.value = (bibassignpreview) => bibassignpreview.Seeding4;
        Seeding4.setValue = () => {};
        Seeding4.DetailEditor = markRaw(InputText);
        Seeding4.DetailEditorProps = {};
        Seeding4.CellEditor = markRaw(InputText);
        Seeding4.CellEditorProps = {};
        Seeding4.CellRenderer = markRaw(ValidatedText);
        Seeding4.CellRendererProps = {};
        Seeding4.Validator = null;
        Seeding4.SortDirection = undefined;
        Seeding4.cellClassRules = undefined;
        Seeding4.MinWidth = undefined;
        Seeding4.MaxWidth = undefined;
        Seeding4.Tooltip = null;
        this.Fields.push(Seeding4);

        const Seeding5 = new FieldDef<BibAssignPreview>();
        Seeding5.Name = 'Seeding5';
        Seeding5.FieldName = 'Seeding5';
        Seeding5.DefinedFor = 'Both';
        Seeding5.TranslationKey = 'bibassignpreview.seeding5';
        Seeding5.Editable = false;
        Seeding5.Filterable = true;
        Seeding5.Sortable = true;
        Seeding5.CssClasses = '';
        Seeding5.value = (bibassignpreview) => bibassignpreview.Seeding5;
        Seeding5.setValue = () => {};
        Seeding5.DetailEditor = markRaw(InputText);
        Seeding5.DetailEditorProps = {};
        Seeding5.CellEditor = markRaw(InputText);
        Seeding5.CellEditorProps = {};
        Seeding5.CellRenderer = markRaw(ValidatedText);
        Seeding5.CellRendererProps = {};
        Seeding5.Validator = null;
        Seeding5.SortDirection = undefined;
        Seeding5.cellClassRules = undefined;
        Seeding5.MinWidth = undefined;
        Seeding5.MaxWidth = undefined;
        Seeding5.Tooltip = null;
        this.Fields.push(Seeding5);

        const Seeding6 = new FieldDef<BibAssignPreview>();
        Seeding6.Name = 'Seeding6';
        Seeding6.FieldName = 'Seeding6';
        Seeding6.DefinedFor = 'Both';
        Seeding6.TranslationKey = 'bibassignpreview.seeding6';
        Seeding6.Editable = false;
        Seeding6.Filterable = true;
        Seeding6.Sortable = true;
        Seeding6.CssClasses = '';
        Seeding6.value = (bibassignpreview) => bibassignpreview.Seeding6;
        Seeding6.setValue = () => {};
        Seeding6.DetailEditor = markRaw(InputText);
        Seeding6.DetailEditorProps = {};
        Seeding6.CellEditor = markRaw(InputText);
        Seeding6.CellEditorProps = {};
        Seeding6.CellRenderer = markRaw(ValidatedText);
        Seeding6.CellRendererProps = {};
        Seeding6.Validator = null;
        Seeding6.SortDirection = undefined;
        Seeding6.cellClassRules = undefined;
        Seeding6.MinWidth = undefined;
        Seeding6.MaxWidth = undefined;
        Seeding6.Tooltip = null;
        this.Fields.push(Seeding6);

        const Seeding7 = new FieldDef<BibAssignPreview>();
        Seeding7.Name = 'Seeding7';
        Seeding7.FieldName = 'Seeding7';
        Seeding7.DefinedFor = 'Both';
        Seeding7.TranslationKey = 'bibassignpreview.seeding7';
        Seeding7.Editable = false;
        Seeding7.Filterable = true;
        Seeding7.Sortable = true;
        Seeding7.CssClasses = '';
        Seeding7.value = (bibassignpreview) => bibassignpreview.Seeding7;
        Seeding7.setValue = () => {};
        Seeding7.DetailEditor = markRaw(InputText);
        Seeding7.DetailEditorProps = {};
        Seeding7.CellEditor = markRaw(InputText);
        Seeding7.CellEditorProps = {};
        Seeding7.CellRenderer = markRaw(ValidatedText);
        Seeding7.CellRendererProps = {};
        Seeding7.Validator = null;
        Seeding7.SortDirection = undefined;
        Seeding7.cellClassRules = undefined;
        Seeding7.MinWidth = undefined;
        Seeding7.MaxWidth = undefined;
        Seeding7.Tooltip = null;
        this.Fields.push(Seeding7);

        const Seeding8 = new FieldDef<BibAssignPreview>();
        Seeding8.Name = 'Seeding8';
        Seeding8.FieldName = 'Seeding8';
        Seeding8.DefinedFor = 'Both';
        Seeding8.TranslationKey = 'bibassignpreview.seeding8';
        Seeding8.Editable = false;
        Seeding8.Filterable = true;
        Seeding8.Sortable = true;
        Seeding8.CssClasses = '';
        Seeding8.value = (bibassignpreview) => bibassignpreview.Seeding8;
        Seeding8.setValue = () => {};
        Seeding8.DetailEditor = markRaw(InputText);
        Seeding8.DetailEditorProps = {};
        Seeding8.CellEditor = markRaw(InputText);
        Seeding8.CellEditorProps = {};
        Seeding8.CellRenderer = markRaw(ValidatedText);
        Seeding8.CellRendererProps = {};
        Seeding8.Validator = null;
        Seeding8.SortDirection = undefined;
        Seeding8.cellClassRules = undefined;
        Seeding8.MinWidth = undefined;
        Seeding8.MaxWidth = undefined;
        Seeding8.Tooltip = null;
        this.Fields.push(Seeding8);

        const Seeding9 = new FieldDef<BibAssignPreview>();
        Seeding9.Name = 'Seeding9';
        Seeding9.FieldName = 'Seeding9';
        Seeding9.DefinedFor = 'Both';
        Seeding9.TranslationKey = 'bibassignpreview.seeding9';
        Seeding9.Editable = false;
        Seeding9.Filterable = true;
        Seeding9.Sortable = true;
        Seeding9.CssClasses = '';
        Seeding9.value = (bibassignpreview) => bibassignpreview.Seeding9;
        Seeding9.setValue = () => {};
        Seeding9.DetailEditor = markRaw(InputText);
        Seeding9.DetailEditorProps = {};
        Seeding9.CellEditor = markRaw(InputText);
        Seeding9.CellEditorProps = {};
        Seeding9.CellRenderer = markRaw(ValidatedText);
        Seeding9.CellRendererProps = {};
        Seeding9.Validator = null;
        Seeding9.SortDirection = undefined;
        Seeding9.cellClassRules = undefined;
        Seeding9.MinWidth = undefined;
        Seeding9.MaxWidth = undefined;
        Seeding9.Tooltip = null;
        this.Fields.push(Seeding9);

        const StartTime = new FieldDef<BibAssignPreview>();
        StartTime.Name = 'StartTime';
        StartTime.FieldName = 'StartTime';
        StartTime.DefinedFor = 'Both';
        StartTime.TranslationKey = 'bibassignpreview.starttime';
        StartTime.Editable = false;
        StartTime.Filterable = true;
        StartTime.Sortable = true;
        StartTime.CssClasses = '';
        StartTime.value = (bibassignpreview) => bibassignpreview.StartTime;
        StartTime.setValue = () => {};
        StartTime.DetailEditor = markRaw(InputNullableDateTime);
        StartTime.DetailEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        StartTime.CellEditor = markRaw(InputNullableDateTime);
        StartTime.CellEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        StartTime.CellRenderer = markRaw(DateComp);
        StartTime.CellRendererProps = { format: 'DATETIME' as DateType, readonly: true };
        StartTime.Validator = null;
        StartTime.SortDirection = undefined;
        StartTime.cellClassRules = undefined;
        StartTime.MinWidth = 180;
        StartTime.MaxWidth = undefined;
        StartTime.Tooltip = null;
        this.Fields.push(StartTime);

        const Blocked = new FieldDef<BibAssignPreview>();
        Blocked.Name = 'Blocked';
        Blocked.FieldName = 'Blocked';
        Blocked.DefinedFor = 'Both';
        Blocked.TranslationKey = 'bibassignpreview.blocked';
        Blocked.Editable = false;
        Blocked.Filterable = false;
        Blocked.Sortable = true;
        Blocked.CssClasses = '';
        Blocked.value = (bibassignpreview) => bibassignpreview.Blocked;
        Blocked.setValue = () => {};
        Blocked.DetailEditor = markRaw(InputBool);
        Blocked.DetailEditorProps = { label: 'bibassignpreview.blocked', readonly: true };
        Blocked.CellEditor = markRaw(InputBool);
        Blocked.CellEditorProps = {};
        Blocked.CellRenderer = markRaw(DisplayBool);
        Blocked.CellRendererProps = {};
        Blocked.Validator = null;
        Blocked.SortDirection = undefined;
        Blocked.cellClassRules = undefined;
        Blocked.MinWidth = undefined;
        Blocked.MaxWidth = undefined;
        Blocked.Tooltip = null;
        this.Fields.push(Blocked);
    }

    public async LoadDependencies() {
        await useContestantStore().EnsureLoaded(['Contestants']);
        await useEventCommonStore().EnsureLoaded(['Groups']);
        await useEventCommonStore().EnsureLoaded(['Categories']);
    }
}
